import React from 'react'
import clsx from 'clsx'
import { Messages2, MessageText, Sms, Note1, More } from 'iconsax-react'
import { useLocation } from 'react-router'
import { useInView } from 'react-hook-inview'
import { useAppDispatch, useAppSelector } from '@SonarCX/hooks/redux'
import { useTheme } from '@mui/material'

import Spacer from '../../atoms/Spacer'
import MediaElementV2 from './MediaElementV2'
// import TagList from '../../atoms/TagList'
import ButtonMenu from '../ButtonMenu'
// import TagInput from '../TagInput'
import MediaAvatar from '../../atoms/MediaAvatar'
import Tooltip from '../../atoms/Tooltip'
import ReadInfo from './ReadInfoV2'
import MessageSkeletonV2 from '../MessageSkeletonV2'
import useLongTouch from '@SonarCX/hooks/useLongTouch'
import SuccessToast from '../SuccessToast'
import ImageGroup from './MediaElementV2/ImageGroup'
import OverflowText from '../../atoms/OverflowText'

import { Routes } from '@SonarCX/Routes'
import { AddErrorAlert } from '@SonarCX/slices/alert'
import { putPatch } from '@SonarCX/lib/api'
import {
  arrayToObject,
  formatMessageTooltipDate,
  replaceMentions,
  loadTaggedUsers
} from '@SonarCX/utilities'
import { countEmojis } from '@SonarCX/utilities/countEmojis'
import transformContent from '@SonarCX/utilities/transformContent'
import { useAgent } from '@SonarCX/providers/AgentProvider'
import { useUserAPI } from '@SonarCX/providers/UserAPIProvider'
import { useSocket } from '@SonarCX/providers/SocketProvider'
import { selectPresence } from '@SonarCX/slices/presence'
import { useChatEditState } from '@SonarCX/providers/ChatEditStateProvider'
import { useBreakpoint } from '@SonarCX/providers/BreakpointProvider'
import { useCopyableState } from '@SonarCX/providers/CopyableStateProvider'
import { formatMessageTimeSince } from '@SonarCX/timeUtilities'

import useStyles from './styles'

const LiveChatIcon = React.memo(() => (
  <Messages2 color='#B64FCD' variant='Bold' />
))
const MessageTextIcon = React.memo(() => (
  <MessageText color='#FFAB07' variant='Bold' />
))
const SmsIcon = React.memo(() => <Sms color='#4E87F5' variant='Bold' />)

const NoteIcon = React.memo(() => (
  <Note1 color='#B64FCD' variant='Bold' style={{ margin: '0 2.5px' }} />
))
const EDIT = 'Edit'
const EDIT_TAGS = 'Edit Tags'
const SAVE = 'Save Changes'
const DISCARD = 'Discard Changes'
const COPY = 'Copy Link'
const COPY_TEXT = 'Copy Text'

const CHANNEL_NAME_MAP = {
  email: 'Email',
  sms_voice: 'SMS'
}

const TYPE_MAP = {
  email: 'Email',
  sms: 'SMS',
  chat: ''
}

const TYPE_MAP_Text = {
  email: 'Email',
  sms: 'SMS',
  chat: 'Live Chat'
}

const getIcon = (type, sideChannelType, isPeerChat) => {
  const ICON_MAP = {
    sms: MessageTextIcon,
    email: SmsIcon
  }

  if (ICON_MAP[type]) {
    return ICON_MAP[type]
  }

  if (type === 'chat' && sideChannelType) {
    if (!ICON_MAP[sideChannelType.toLowerCase()]) return () => null
    return ICON_MAP[sideChannelType.toLowerCase()]
  }

  if (type === 'chat' && !isPeerChat) {
    return LiveChatIcon
  }

  return () => null
}

const MessageV2 = (
  {
    content,
    sender,
    senderFull,
    media,
    tags = [],
    allTags,
    type,
    date,
    isPrivateNote,
    id,
    sessionID,
    meta,
    correlationId,
    received,
    expired,
    className,
    lastRead,
    p2p,
    recentActivity,
    above,
    below,
    isNew,
    convo,
    isDirect,
    isCurrent
  },
  ref
) => {
  const dispatch = useAppDispatch()
  // Gets props from ChatWindow
  const location = useLocation()
  const [inViewRef, inView, observer] = useInView({
    root: document.querySelector('#chat'),
    rootMargin: '100px',
    threshold: 0,
    unobserveOnEnter: true
  })
  const theme = useTheme()

  const { currentUser } = useAgent()
  const { findChannels, allUsers, loadUser } = useUserAPI()
  const {
    api: { handlePatch },
    retry: { msgMap }
  } = useSocket()
  const { presence } = useAppSelector(selectPresence)
  const { isSmall } = useBreakpoint()
  const { setText, copyToClipboard } = useCopyableState()
  const {
    edit: startEdit,
    end: endEdit,
    state: chatEditState
  } = useChatEditState()
  const isBeingEdited = React.useMemo(
    () =>
      chatEditState.originalMessage && chatEditState.originalMessage.id === id,
    [chatEditState, id]
  )
  const [editing, setEditing] = React.useState(isBeingEdited)
  const [editingTags, setEditingTags] = React.useState(isBeingEdited)
  const [hovering, setHovering] = React.useState(false)
  const [tagString, setTagString] = React.useState(tags.join(','))
  const [body, setBody] = React.useState(content)
  const [linkCopied, setLinkCopied] = React.useState(false)
  const [user, setUser] = React.useState(null)
  const [online, setOnline] = React.useState(false)
  const messageContentRef = React.useRef()
  const [taggedUsersLoaded, setTaggedUsersLoaded] = React.useState(false)
  const [menuOpen, setMenuOpen] = React.useState(false)
  const [visible, setVisible] = React.useState(false)
  const { position: mousePosition, ...longTouchProps } = useLongTouch(() => {
    setMenuOpen(true)
    setHovering(false)
  })

  const formattedTime = React.useMemo(() => {
    try {
      return formatMessageTimeSince(new Date(date), Date.now())
    } catch {
      console.warn('Invalid date for Message', date)
      return formatMessageTimeSince(Date.now(), Date.now())
    }
  }, [date])

  const yourID = React.useMemo(() => currentUser()?.id || null, [currentUser])
  const isYours = React.useMemo(
    () => (senderFull ? senderFull.id === yourID : true),
    [senderFull, yourID]
  )

  const showAvatar = (!above && below) || (!above && !below)
  const middleMsg = above && below
  const startGroup = !above && below
  const endGroup = above && !below

  // Counts how many emojis are in emoji only strings
  const emojiCount = React.useMemo(() => {
    if (!content) {
      return 0
    }

    // Partly from here: https://stackoverflow.com/questions/41079416/javascript-detect-if-a-string-contains-only-unicode-emojis
    const emojiRe = /\p{Extended_Pictographic}/gu
    const removeEmoji = str => {
      return str.replace(emojiRe, '')
    }
    const removed = removeEmoji(content).trim()

    // Some emoji strings have only invisible characters left
    const isOnlyEmojis = !removed.length || removed === '‍‍'

    if (isOnlyEmojis) {
      const count = countEmojis(content)
      return count
    }

    return 0
  }, [content])

  const classes = useStyles({
    flip: isYours,
    middleMsg,
    startGroup,
    endGroup,
    showAvatar,
    emojiCount
  })

  // const read = date < lastRead

  React.useEffect(() => {
    if (!chatEditState.originalMessage) {
      setEditing(false)
      setEditingTags(false)
      return
    }
    if (
      chatEditState.originalMessage &&
      chatEditState.originalMessage.id !== id
    ) {
      setEditing(false)
      setEditingTags(false)
    }
  }, [chatEditState.originalMessage, id])

  React.useEffect(() => {
    setUser(allUsers[senderFull?.id])
  }, [allUsers, senderFull])

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (taggedUsersLoaded) {
      return
    }

    const message = content ?? ''
    loadTaggedUsers(message, loadUser)

    setTaggedUsersLoaded(true)
  }, [taggedUsersLoaded, content, loadUser])

  // checking p2p chats if other user is online
  React.useEffect(() => {
    if (p2p && sessionID && presence[sessionID]) {
      setOnline(true)
    }
  }, [sessionID, p2p, presence, online])

  // Add edited to the end of the markdown
  React.useEffect(() => {
    if ((meta?.body_history?.length ?? 0) === 0) {
      return
    }

    const base = messageContentRef.current
    if (!base) {
      return
    }

    const message = base.firstChild?.firstChild
    if (!message) {
      return
    }

    if (message.lastChild?.className === classes.edited) {
      return
    }

    // Add the (edited) to the generated markdown, once only
    const editedNode = document.createElement('span')
    editedNode.className = classes.edited
    editedNode.textContent = '(edited)'
    message.appendChild(editedNode)
  })

  const options = React.useMemo(() => {
    let opts = []

    if (location.pathname.startsWith(Routes.CLIENT_CHATS)) {
      opts = [COPY]
    }

    if (editing || editingTags) {
      opts = [SAVE, DISCARD]
    } else if (isYours) {
      opts = [EDIT, ...opts]
    } else {
      // opts = [EDIT_TAGS, ...opts]
    }

    if (!editing && !editingTags) {
      opts = [...opts, COPY_TEXT]
    }

    return opts
  }, [location, editing, editingTags, isYours])

  // Reset hovering state
  React.useEffect(() => {
    setHovering(false)
  }, [menuOpen])

  // Stores content for copying
  React.useEffect(() => {
    if (!content) {
      setText('')
    } else {
      const agents = Object.values({
        ...allUsers,
        ...arrayToObject(convo?.fullUsers ?? [])
      })
      const transformed = replaceMentions(content, agents)
      const withoutMultipleSpaces = transformed.replace(/ +/gm, ' ')
      setText(withoutMultipleSpaces)
    }
  }, [content, allUsers, convo, setText])

  // See if message should be shown, sometimes hook breaks when switching rapidly between sessions
  // eslint-disable-next-line
  React.useEffect(() => {
    if (visible || inView) {
      return
    }

    if (!observer) {
      return
    }

    // Check if the message is within chat timeline
    const rect = observer.target?.getBoundingClientRect()
    const chat = document.querySelector('#chat')?.getBoundingClientRect()
    if (rect && chat) {
      const inside = chat.top <= rect.y && rect.y <= chat.bottom

      if (inside) {
        setVisible(true)
      }
    }
  })

  if (media?.length === 0 && !content) {
    return null
  }

  const pushChanges = () => {
    const updatedData = { content: { body: content }, tags }

    if (editing) {
      const contentBody = body?.trim() || null
      updatedData.content = { body: contentBody }
    }

    const newTags = tagString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0)
    const uniqueTags = [...new Set(newTags)]
    updatedData.tags = uniqueTags

    const data = { ...updatedData, action: 'chat' }
    putPatch(id, data)
      .then(data => {
        setEditing(false)
        setEditingTags(false)
        endEdit()
        handlePatch(data)
      })
      .catch(err => {
        console.error('Error putting patch', err)
        dispatch(AddErrorAlert({ err }))
      })
  }

  const copyLinkToClipboard = () => {
    const link = `${window.location.href}?session=${sessionID}&id=${id}`
    navigator.clipboard.writeText(link).then(() => setLinkCopied(true))
  }

  const handleMenuSelect = item => {
    if (item === EDIT) {
      setBody(content)
      //setTagString(tags.join(','))
      setEditing(true)
      startEdit(id)
    } else if (item === EDIT_TAGS) {
      setTagString(tags.join(','))
      setEditingTags(true)
    } else if (item === SAVE) {
      pushChanges()
    } else if (item === DISCARD) {
      setEditing(false)
      setEditingTags(false)
      endEdit()
    } else if (item === COPY) {
      copyLinkToClipboard()
    } else if (item === COPY_TEXT) {
      copyToClipboard()
    }

    setMenuOpen(false)
  }

  const handleLinkCopyClose = () => setLinkCopied(false)

  const onlyImages = media?.length >= 1 && media.every(m => m.type === 'image')

  const messageClass = isPrivateNote ? classes.note : classes.message

  let sideChannelString = null
  let sideChannelType = null
  if (!isPrivateNote) {
    let sideChannels = []
    if (meta?.side_channels?.length > 0) {
      sideChannels = meta.side_channels
    }

    if (sideChannels.length > 0) {
      const channelsObj = findChannels(sideChannels.map(({ id }) => id))
      if (channelsObj.length > 0) {
        sideChannelString = `Sent by ${channelsObj
          .map(
            ({ name, channel }) =>
              `${CHANNEL_NAME_MAP[channel.type]} to ${name}`
          )
          .join(', by ')}`
        sideChannelType = `${channelsObj.map(
          ({ channel }) => `${CHANNEL_NAME_MAP[channel.type]}`
        )}`
      }
    }
  }

  // get mapped objects from socket
  const msgPayload = [...msgMap.current.values()]

  // finding index of msg to retry / replace
  const getIndex = () => {
    return msgPayload.findIndex(
      msg => msg.payload.correlationId === correlationId
    )
  }

  const restartTimer = () => {
    const index = getIndex()

    msgPayload[index].count = 0

    const exist = msgMap.current.get(correlationId)

    msgMap.current.set(correlationId, {
      ...exist,
      timer: setInterval(() => {
        exist['timer function'](correlationId)
      }, 5000)
    })
    return
  }

  // showing message received status
  const sent = () => {
    if (!received || expired) {
      return <span className={classes.seen}> &middot; sent</span>
    } else if (received) {
      return seen()
    }
  }

  const seen = () => {
    if ((p2p && !isYours) || recentActivity) {
      return null
    } else {
      return <span className={classes.seen}> &middot; seen</span>
    }
  }

  const retryError = () => {
    const index = getIndex()

    if (msgPayload[index]?.count === 2) {
      return (
        <div className={classes.errorMsg}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginLeft: '0.25rem',
              marginRight: '0.25rem',
              cursor: 'default'
            }}
          >
            <strong style={{ cursor: 'default' }}>Message error</strong>
          </div>
          {msgPayload[index]?.count === 2 && (
            <div
              style={{ fontWeight: theme.bold.bolder }}
              onClick={() => restartTimer()}
              className={classes.retry}
            >
              Retry
            </div>
          )}
        </div>
      )
    }
  }

  const isPeerChat = isDirect && !convo?.isGroup

  const Icon = getIcon(type, sideChannelType, isPeerChat)

  const info = (
    <ReadInfo
      date={date}
      session={sessionID}
      sender={senderFull?.id}
      isPeerChat={isPeerChat}
    />
  )
  const ttContent = (
    <>
      {isPeerChat ? (isYours ? info : null) : info}

      <div>Sent on {formatMessageTooltipDate(date)}</div>

      {sideChannelString && <div>{sideChannelString}</div>}
    </>
  )
  const formattedSender = user && user.name !== sender ? user.name : sender
  return (
    <>
      <div
        className={clsx(
          classes.root,
          className,
          middleMsg && classes.middleMsg,
          startGroup && classes.start,
          endGroup && classes.end
        )}
        ref={ref}
      >
        <div ref={inViewRef}>
          {inView || visible || isCurrent ? (
            <>
              <>
                {showAvatar && (
                  <div className={clsx(classes.row)}>
                    <MediaAvatar
                      user={user ?? senderFull}
                      className={classes.avatar}
                      disableAnimation
                    />
                    <div
                      className={clsx(
                        classes.messageBase,
                        classes.fullWidth,
                        classes.truncate,
                        messageClass
                      )}
                    >
                      <div className='header'>
                        <div className='info'>
                          {/* Don't show on peer chats */}
                          {!isPeerChat && (
                            <OverflowText className='name'>
                              {isYours ? `You` : formattedSender}
                            </OverflowText>
                          )}

                          <Tooltip title={ttContent} placement='bottom' arrow>
                            <div className='rest'>
                              {isPrivateNote ? (
                                <div>
                                  Private Note
                                  <NoteIcon />
                                  <span className={classes.sentTime}>
                                    {formattedTime}
                                  </span>
                                </div>
                              ) : (
                                <>
                                  <div>
                                    <span className={classes.sentMethod}>
                                      <span className={classes.truncate}>
                                        Sent by{' '}
                                        {type === 'chat' && sideChannelType
                                          ? sideChannelType
                                          : TYPE_MAP_Text[type] ?? ''}
                                      </span>
                                      <Icon className={classes.icon} />
                                      &middot;
                                    </span>
                                    {type && TYPE_MAP[type]}{' '}
                                    <span className={classes.sentTime}>
                                      {formattedTime}
                                    </span>
                                  </div>
                                  {correlationId ? sent() : seen()}
                                </>
                              )}
                            </div>
                          </Tooltip>
                        </div>
                      </div>

                      <div>{retryError()}</div>
                    </div>

                    <Spacer />
                  </div>
                )}
              </>
              {content && (
                <div
                  className={clsx(
                    classes.row,
                    !isSmall && classes.bottomRow,
                    media.length && classes.hasContentAndMedia
                  )}
                  onMouseEnter={() => setHovering(true)}
                  onMouseLeave={() => setHovering(false)}
                  {...longTouchProps}
                >
                  <div className={clsx(classes.messageBase, messageClass)}>
                    <div className='box'>
                      <Spacer />

                      <div>
                        {(content || meta?.body_history?.length > 0) && (
                          <div
                            ref={messageContentRef}
                            className={classes.msgContent}
                            style={{ width: editingTags ? '200px' : 'unset' }}
                          >
                            {content
                              ? transformContent(
                                  content,
                                  classes.mention,
                                  {
                                    ...allUsers,
                                    ...arrayToObject(convo?.fullUsers ?? [])
                                  },
                                  currentUser()?.name,
                                  classes.mentionYou
                                )
                              : ''}
                          </div>
                        )}

                        {/* {editing || editingTags ? (
                          <TagInput
                            id={id}
                            value={tagString}
                            onChange={e => setTagString(e.target.value)}
                            label='Enter tags'
                            tags={allTags}
                          />
                        ) : (
                          <TagList tags={tags} />
                        )} */}
                      </div>
                    </div>
                  </div>

                  <ButtonMenu
                    id={`message-select-${id}`}
                    data-testid={`message-select-${id}`}
                    options={options}
                    onSelect={handleMenuSelect}
                    className={classes.dots}
                    manual
                    open={menuOpen}
                    onOpen={() => setMenuOpen(true)}
                    onClose={() => setMenuOpen(false)}
                    anchorReference={
                      mousePosition ? 'anchorPosition' : 'anchorEl'
                    }
                    anchorPosition={{
                      left: (mousePosition?.x ?? 0) - 50,
                      top: (mousePosition?.y ?? 0) - 25
                    }}
                  >
                    {!mousePosition && (
                      <More
                        className='dot'
                        style={{
                          transform: 'rotate(90deg)',
                          width: '1rem',
                          height: '1rem',
                          visibility:
                            hovering && options.length > 0
                              ? 'visible'
                              : 'hidden'
                        }}
                      />
                    )}
                  </ButtonMenu>

                  <Spacer />
                </div>
              )}
              {onlyImages ? (
                <>
                  <div
                    className={clsx(
                      classes.row,
                      classes.mediaRow,
                      !isSmall && classes.bottomRow
                    )}
                  >
                    <div className={clsx(classes.messageBase, messageClass)}>
                      <div className='media-box'>
                        <div>
                          <ImageGroup
                            media={media}
                            observer={observer}
                            isYours={isYours}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {media.map(m => {
                    return (
                      <div
                        key={m.id}
                        className={clsx(
                          classes.row,
                          classes.mediaRow,
                          !isSmall && classes.bottomRow
                        )}
                      >
                        <div
                          className={clsx(classes.messageBase, messageClass)}
                        >
                          <div className='media-box'>
                            <div>
                              <MediaElementV2
                                onlyImages={onlyImages}
                                media={m}
                                observer={observer}
                                isYours={isYours}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </>
          ) : (
            <MessageSkeletonV2 isYours={isYours} />
          )}
        </div>
      </div>

      {linkCopied && (
        <SuccessToast
          onClose={handleLinkCopyClose}
          message='Link copied to clipboard.'
        />
      )}
    </>
  )
}

export default React.forwardRef(MessageV2)
